import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from "@angular/core";
import { TickerInfo } from "src/app/models/ticker";
import { Currency } from "src/app/models/currency";
import { AuthService } from "src/app/api/auth.service";
import { environment } from "src/environments/environment";
import { PromotionsService } from "src/app/api/promotions.service";
import { VerifyAndEarnPromotion } from "src/app/models/promotions";
import { LocaleService } from "src/app/services/locale.service";
import { GeneralEvents } from "src/app/models/tracking-analytics";
import { AmplitudeAnalyticsService } from "src/app/services/amplitude-analytics.service";
import { catchError, map } from "rxjs/operators";
import { ThemeService } from "src/app/services/theme.service";
import { Theme } from "src/app/models/theme";
import { CurrenciesService } from "src/app/api/currencies.service";
import { MetadataService } from "src/app/api/metadata.service";
import { TickerService } from "src/app/api/ticker.service";
import { forkJoin, of } from "rxjs";

export interface TopMover {
  code: string;
  name: string;
  pair: string;
  price: number;
  usdPrice: number;
  fiatCurrency: string;
  changePercent: number;
}

@Component({
    selector: "app-home",
    templateUrl: "./home.component.html",
    styleUrls: ["./home.component.scss"],
    standalone: false
})
export class HomeComponent implements OnInit, AfterViewInit {
  @ViewChild("heroVideo") heroVideo: ElementRef;

  readonly MOBILE_BREAK_POINT = 768;
  readonly skeletonMovers = Array.from({ length: 5 });

  currencies: Currency[] = [];

  theme$ = this.themeService.themeChanges.pipe(map((it) => it));

  loading = false;
  error: string = null;

  promotion: VerifyAndEarnPromotion;
  isMobile = false;
  links = environment.links;
  currentLanguage: string;
  GeneralEvents = GeneralEvents;

  topGainers: TopMover[] = [];
  topLosers: TopMover[] = [];
  fiatCurrency = "";
  moversLoading = true;

  constructor(
    private localeService: LocaleService,
    private auth: AuthService,
    private promotionsService: PromotionsService,
    private amplitudeAnalytics: AmplitudeAnalyticsService,
    private themeService: ThemeService,
    private currenciesService: CurrenciesService,
    private tickerService: TickerService,
    private metadataService: MetadataService,
  ) {
    this.currentLanguage = this.localeService.currentLocale;
  }

  ngOnInit() {
    this.themeService.set(Theme.Light, false);
    this.getPromotions();
    this.loadTopMovers();
  }
  ngAfterViewInit(): void {
    this.onResize();
  }
  getPromotions() {
    this.loading = true;

    const currencies$ = this.currenciesService.getCrypto();
    const verifyAndEarn$ = this.promotionsService.verifyAndEarn();

    forkJoin({
      verifyAndEarn: verifyAndEarn$,
      currencies: currencies$,
    })
      .pipe(
        map(({ verifyAndEarn, currencies }) => {
          this.loading = false;
          this.promotion = verifyAndEarn;
          this.currencies = currencies;
        }),
      )
      .subscribe();
  }

  isLoggedIn() {
    return this.auth.getToken();
  }

  playVideo() {
    const video = this.heroVideo ? this.heroVideo.nativeElement : null;
    if (video) {
      video.muted = true;

      video.play().catch((error) => {
        console.error("Video playback failed:", error);
      });

      const onCanPlayThrough = () => {
        video.play().catch((error) => {
          console.error("Video playback failed after canplaythrough:", error);
        });
        video.removeEventListener("canplaythrough", onCanPlayThrough);
      };

      video.addEventListener("canplaythrough", onCanPlayThrough);
    }
  }

  onAppDownloadClick(event: GeneralEvents) {
    this.amplitudeAnalytics.SendTrack(event, {});
  }

  trackByMover(_: number, mover: TopMover) {
    return mover.code;
  }

  private loadTopMovers() {
    forkJoin({
      fiatCurrency: this.metadataService
        .userCurrency()
        .pipe(catchError(() => of(environment.currencies.defaultCurrency))),
      tickers: this.tickerService
        .getAllData(false, false, false)
        .pipe(catchError(() => of([] as TickerInfo[]))),
    }).subscribe(
      ({ fiatCurrency, tickers }) => {
        this.fiatCurrency = fiatCurrency;
        this.applyMovers(fiatCurrency, tickers);
      },
      () => {
        this.moversLoading = false;
      },
    );
  }

  private applyMovers(fiat: string, tickers: TickerInfo[]) {
    const fiatSuffix = `-${fiat}`;
    const movers: TopMover[] = tickers
      .filter(
        (t) =>
          t?.pairInfo?.pair?.endsWith(fiatSuffix) &&
          t?.data?.changePercentage24hour != null,
      )
      .map((t) => {
        const pair = t.pairInfo.pair;
        const code = pair.split("-")[0];
        return {
          code,
          name: code,
          pair,
          price: parseFloat(t.data.price) || 0,
          usdPrice: parseFloat(t.usdData?.price) || 0,
          fiatCurrency: fiat,
          changePercent: t.data.changePercentage24hour || 0,
        } as TopMover;
      })
      .filter((m) => m.changePercent !== 0);

    this.topGainers = movers
      .filter((m) => m.changePercent > 0)
      .sort((a, b) => b.changePercent - a.changePercent)
      .slice(0, 5);

    this.topLosers = movers
      .filter((m) => m.changePercent < 0)
      .sort((a, b) => a.changePercent - b.changePercent)
      .slice(0, 5);

    this.moversLoading = false;
  }

  @HostListener("window:resize")
  onResize() {
    const width = window.screen.width;

    if (width <= this.MOBILE_BREAK_POINT) {
      this.isMobile = true;
    } else {
      this.isMobile = false;
      this.playVideo();
    }
  }
}
